import { useEffect, useState, type ChangeEvent } from 'react';
import {
  confirmProjectImport,
  createProjectExport,
  downloadProjectTransfer,
  getProjectTransferInventory,
  getProjectTransferJob,
  uploadProjectImport,
  type ClientProjectTransferInventory,
  type ClientProjectTransferJob,
} from './api';

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

function formatBytes(value: number) {
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${Math.round(value / 1024)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function TransferError({ message }: { message: string }) {
  return <p className="project-transfer-error" role="alert">{message}</p>;
}

function TransferProgress({ job }: { job: ClientProjectTransferJob }) {
  if (job.status === 'queued') return <p className="project-transfer-progress" role="status">已加入处理队列…</p>;
  if (job.status === 'running') return <p className="project-transfer-progress" role="status">正在处理… {job.progress}%</p>;
  if (job.status === 'failed') return <TransferError message={job.errorMessage || '处理失败，请重试。'} />;
  return null;
}

export function ProjectExportModal({ projectId, projectTitle, onClose }: { projectId: string; projectTitle: string; onClose: () => void }) {
  const [inventory, setInventory] = useState<ClientProjectTransferInventory>();
  const [images, setImages] = useState(false);
  const [audio, setAudio] = useState(false);
  const [textFormat, setTextFormat] = useState<'structured' | 'source'>('structured');
  const [job, setJob] = useState<ClientProjectTransferJob>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    void getProjectTransferInventory(projectId).then((value) => { if (active) setInventory(value); }).catch((cause) => { if (active) setError(cause instanceof Error ? cause.message : '读取素材信息失败。'); });
    return () => { active = false; };
  }, [projectId]);

  useEffect(() => {
    if (!job || (job.status !== 'queued' && job.status !== 'running')) return;
    let active = true;
    const poll = async () => {
      try {
        const next = await getProjectTransferJob(job.id);
        if (!active) return;
        setJob(next);
        if (next.status === 'queued' || next.status === 'running') window.setTimeout(poll, 1000);
      } catch (cause) {
        if (active) setError(cause instanceof Error ? cause.message : '读取导出进度失败。');
      }
    };
    const timer = globalThis.setTimeout(poll, 500);
    return () => { active = false; globalThis.clearTimeout(timer); };
  }, [job]);

  async function submit() {
    setBusy(true);
    setError('');
    try {
      const result = await createProjectExport(projectId, { images, audio, textFormat });
      if (result.kind === 'text') {
        downloadBlob(result.blob, `${projectTitle || '小说'}.txt`);
        onClose();
      } else {
        setJob(result.job);
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : '创建导出任务失败。');
    } finally {
      setBusy(false);
    }
  }

  async function downloadZip() {
    if (!job) return;
    setBusy(true);
    try {
      downloadBlob(await downloadProjectTransfer(job.id), `${projectTitle || '小说'}.zip`);
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : '下载压缩包失败。');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation">
      <section className="continuation-modal project-transfer-modal" role="dialog" aria-modal="true" aria-labelledby="project-export-title">
        <div className="modal-heading"><div><span className="section-kicker">导出小说</span><h2 id="project-export-title">保存你的故事。</h2></div><button className="modal-close" type="button" onClick={onClose}>×</button></div>
        <p className="modal-description">小说内容是导出的基础，插图和配音可以按需加入。</p>
        <label className="project-transfer-format"><span>小说文字格式</span><select aria-label="导出格式" value={textFormat} onChange={(event) => setTextFormat(event.target.value as 'structured' | 'source')}><option value="structured">导入格式（含 [END_SCENE]）</option><option value="source" disabled={images || audio}>小说源文（普通阅读）</option></select><small>{images || audio ? '包含媒体时必须使用导入格式，便于恢复章节和素材。' : '导入格式适合再次导入，小说源文适合普通阅读。'}</small></label>
        <div className="project-transfer-options">
          <label><input type="checkbox" checked disabled /> <span><strong>小说内容</strong><small>标题、简介、人物和正文</small></span></label>
          <label><input type="checkbox" checked={images} onChange={(event) => { setImages(event.target.checked); if (event.target.checked) setTextFormat('structured'); }} /> <span><strong>当前插图</strong><small>{inventory ? `${inventory.imageCount} 张 · ${formatBytes(inventory.imageBytes)}` : '读取中…'}</small></span></label>
          <label><input type="checkbox" checked={audio} onChange={(event) => { setAudio(event.target.checked); if (event.target.checked) setTextFormat('structured'); }} /> <span><strong>当前配音</strong><small>{inventory ? `${inventory.audioSceneCount} 幕 · 连续音频 · ${formatBytes(inventory.audioBytes)}` : '读取中…'}</small></span></label>
        </div>
        {job && <TransferProgress job={job} />}
        {error && <TransferError message={error} />}
        <div className="modal-actions">
          <button className="modal-cancel" disabled={busy} type="button" onClick={onClose}>取消</button>
          {job?.status === 'succeeded' ? <button className="modal-submit" disabled={busy} type="button" onClick={() => void downloadZip()}>{busy ? '准备下载…' : '下载压缩包 →'}</button> : <button className="modal-submit" disabled={busy || Boolean(job && (job.status === 'queued' || job.status === 'running'))} type="button" onClick={() => void submit()}>{busy ? '正在准备…' : images || audio ? '开始打包 →' : '导出 TXT →'}</button>}
        </div>
      </section>
    </div>
  );
}

export function ProjectImportModal({ onClose, onImported }: { onClose: () => void; onImported: (projectId?: string) => void }) {
  const [file, setFile] = useState<File>();
  const [job, setJob] = useState<ClientProjectTransferJob>();
  const [preview, setPreview] = useState<ClientProjectTransferJob['preview']>();
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!confirmed || !job || (job.status !== 'queued' && job.status !== 'running')) return;
    let active = true;
    const poll = async () => {
      try {
        const next = await getProjectTransferJob(job.id);
        if (!active) return;
        setJob(next);
        if (next.status === 'succeeded') onImported(next.preview?.projectId);
        else if (next.status === 'queued' || next.status === 'running') globalThis.setTimeout(poll, 1000);
      } catch (cause) {
        if (active) setError(cause instanceof Error ? cause.message : '读取导入进度失败。');
      }
    };
    const timer = globalThis.setTimeout(poll, 500);
    return () => { active = false; globalThis.clearTimeout(timer); };
  }, [confirmed, job, onImported]);

  function chooseFile(event: ChangeEvent<HTMLInputElement>) {
    setFile(event.target.files?.[0]);
    setPreview(undefined);
    setJob(undefined);
    setConfirmed(false);
    setError('');
  }

  async function upload() {
    if (!file) return;
    setBusy(true);
    setError('');
    try {
      const result = await uploadProjectImport(file);
      setJob(result.job);
      setPreview(result.preview);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : '文件检查失败。');
    } finally {
      setBusy(false);
    }
  }

  async function confirm() {
    if (!job) return;
    setBusy(true);
    setError('');
    try {
      setJob(await confirmProjectImport(job.id));
      setConfirmed(true);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : '导入任务创建失败。');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation">
      <section className="continuation-modal project-transfer-modal" role="dialog" aria-modal="true" aria-labelledby="project-import-title">
        <div className="modal-heading"><div><span className="section-kicker">导入小说</span><h2 id="project-import-title">继续你的故事。</h2></div><button className="modal-close" type="button" onClick={onClose}>×</button></div>
        <p className="modal-description">支持平台导出的 TXT 或 ZIP。导入会创建一部新小说，不会覆盖原内容。</p>
        <label className="project-transfer-file"><span>选择文件</span><input type="file" accept=".txt,.zip,text/plain,application/zip" onChange={chooseFile} /><small>{file?.name || '请选择 TXT 或 ZIP 文件'}</small></label>
        {preview && <div className="project-transfer-preview"><strong>{preview.title}</strong><span>{preview.sceneCount} 幕 · {preview.characterCount} 名人物 · {preview.imageCount || 0} 张图片 · {preview.audioSceneCount || 0} 幕配音</span></div>}
        {job && <TransferProgress job={job} />}
        {error && <TransferError message={error} />}
        <div className="modal-actions"><button className="modal-cancel" disabled={busy} type="button" onClick={onClose}>取消</button>{preview && job?.status === 'queued' ? <button className="modal-submit" disabled={busy} type="button" onClick={() => void confirm()}>{busy ? '正在提交…' : '确认导入 →'}</button> : <button className="modal-submit" disabled={busy || !file} type="button" onClick={() => void upload()}>{busy ? '正在检查…' : '检查文件 →'}</button>}</div>
      </section>
    </div>
  );
}
